import { getShortcuts } from './registry'
import { topScope, topScopeId } from './scopes'
import type { Shortcut } from './types'

const SEQUENCE_TIMEOUT_MS = 1000

interface MatcherState {
    sequence: string[]
    timer: ReturnType<typeof setTimeout> | null
}

/**
 * Extract the list of unique single atoms from a set of shortcuts.
 * An atom is either a single combo ("$mod+Enter", "?", "g") or one
 * space-separated step of a sequence ("g i" → ["g", "i"]).
 */
export function atomsForShortcuts(shortcuts: Iterable<Shortcut>): string[] {
    const set = new Set<string>()
    for (const s of shortcuts) {
        for (const atom of s.keys.split(/\s+/).filter(Boolean)) {
            set.add(atom)
        }
    }
    return Array.from(set)
}

export interface MatcherContext {
    inInput: boolean
}

/**
 * Is this shortcut's scope the one currently holding the keyboard? Exported so
 * the help overlay lists exactly what can fire, rather than restating this and
 * drifting from it.
 */
export function isScopeActive(shortcut: Shortcut): boolean {
    if (shortcut.scope === 'global') return true
    if (shortcut.scope !== topScope()) return false
    // A blurred screen stays mounted and keeps its shortcuts registered (on web
    // `freezeOnBlur` only hides the subtree), so the live screen is not the only
    // owner of its scope's keys — mail's list and a Boards board both register
    // 'list' j/k/x. Only the instance holding the keyboard may fire.
    //
    // An owner's id is assigned on MOUNT and never changes, so the stamp always
    // names the instance that registered the shortcut — never whoever happened
    // to hold the scope when a re-registration fired. That distinction is the
    // whole point: on web a blurred screen stays mounted (`freezeOnBlur` only
    // hides the subtree), its live queries keep emitting, and it re-registers on
    // its own schedule. Re-deriving the stamp then handed the BLURRED screen the
    // FOCUSED screen's id, and — being earlier in the registry's insertion order
    // — it won the match and swallowed the keypress.
    //
    // `scopeId === undefined` means the shortcut was registered with no scope
    // owner at all; it keeps the original scope-only behavior.
    if (shortcut.scopeId === undefined) return true
    return shortcut.scopeId === topScopeId()
}

function matches(shortcut: Shortcut, ctx: MatcherContext): boolean {
    if (!isScopeActive(shortcut)) return false
    if (ctx.inInput && !shortcut.allowInInputs) return false
    if (shortcut.when && !shortcut.when()) return false
    return true
}

/**
 * Find a registered shortcut that exactly matches the given keys string.
 * Returns the first match in iteration order.
 */
function findExactMatch(keys: string, ctx: MatcherContext): Shortcut | null {
    for (const shortcut of getShortcuts().values()) {
        if (shortcut.keys !== keys) continue
        if (!matches(shortcut, ctx)) continue
        return shortcut
    }
    return null
}

/**
 * Does any registered shortcut's keys string *start with* the given sequence,
 * with more atoms to come? Used to decide whether to keep buffering.
 */
function hasPrefixMatch(prefix: string, ctx: MatcherContext): boolean {
    const prefixAtoms = prefix.split(/\s+/).filter(Boolean)
    for (const shortcut of getShortcuts().values()) {
        const keyAtoms = shortcut.keys.split(/\s+/).filter(Boolean)
        if (keyAtoms.length <= prefixAtoms.length) continue
        if (!matches(shortcut, ctx)) continue
        let ok = true
        for (let i = 0; i < prefixAtoms.length; i++) {
            if (keyAtoms[i] !== prefixAtoms[i]) {
                ok = false
                break
            }
        }
        if (ok) return true
    }
    return false
}

export function createMatcher() {
    const state: MatcherState = { sequence: [], timer: null }

    function reset() {
        state.sequence = []
        if (state.timer) {
            clearTimeout(state.timer)
            state.timer = null
        }
    }

    function armTimer() {
        if (state.timer) clearTimeout(state.timer)
        state.timer = setTimeout(reset, SEQUENCE_TIMEOUT_MS)
    }

    /**
     * Feed a single key atom into the matcher. Returns true if the atom was
     * consumed (either fired a shortcut or is building toward a sequence).
     * Callers should preventDefault when true.
     */
    function feedAtom(atom: string, ctx: MatcherContext): boolean {
        const candidate = [...state.sequence, atom].join(' ')

        const exact = findExactMatch(candidate, ctx)
        if (exact) {
            reset()
            exact.run({ keys: candidate })
            return true
        }

        if (hasPrefixMatch(candidate, ctx)) {
            state.sequence.push(atom)
            armTimer()
            return true
        }

        // The atom didn't match any exact or prefix — if we were mid-sequence,
        // reset and try the atom again as a fresh first atom.
        if (state.sequence.length > 0) {
            reset()
            return feedAtom(atom, ctx)
        }

        return false
    }

    return { feedAtom, reset, getSequence: () => [...state.sequence] }
}

export type Matcher = ReturnType<typeof createMatcher>
